import {
  Autocomplete,
  DataGrid,
  Form,
  useNotifications
} from '@ijoin/components';
import {
  useDeleteAdminAdvisoryOrganizationIntegration,
  useGetAdminAdvisoryOrganizationIntegrations,
  useGetAdminAdvisoryOrganizationUnlinkedIntegrations,
  usePostAdminAdvisoryOrganizationIntegration
} from '@ijoin/hooks';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useForm } from 'react-hook-form';

import { LinkButton, OrgIntegrationActionsCell } from '../../../components';

const IntegrationsTab = () => {
  const { advisoryOrganizationId } = Route.useParams();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const linked = useGetAdminAdvisoryOrganizationIntegrations(
    advisoryOrganizationId
  );
  const unlinked = useGetAdminAdvisoryOrganizationUnlinkedIntegrations(
    advisoryOrganizationId
  );

  type IntegrationOption = { label: string; value: string };

  const linkForm = useForm<{ integration: IntegrationOption | null }>({
    defaultValues: { integration: null }
  });
  const integrationToLink = linkForm.watch('integration');

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey.some(
          key =>
            typeof key === 'string' &&
            key.startsWith(
              `/api/v3/admin/advisory-organizations/${advisoryOrganizationId}`
            )
        )
    });
  };

  const linkIntegration = usePostAdminAdvisoryOrganizationIntegration({
    mutation: {
      onError: () => notifications.error('Failed to link integration'),
      onSuccess: () => {
        invalidate();
        linkForm.setValue('integration', null);
        notifications.success('Integration linked');
      }
    }
  });
  const unlinkIntegration = useDeleteAdminAdvisoryOrganizationIntegration({
    mutation: {
      onError: () => notifications.error('Failed to unlink integration'),
      onSuccess: () => {
        invalidate();
        notifications.success('Integration unlinked');
      }
    }
  });

  const columns: GridColDef[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Description',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.description)}
          params={{ integrationId: params.row.integrationId }}
          to='/integrations/$integrationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'integrationTypeId',
      flex: 1,
      headerName: 'Type',
      valueGetter: (_value, row) =>
        row.IntegrationTypes?.description ?? row.integrationTypeId
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <OrgIntegrationActionsCell
          description={params.row.description}
          onUnlink={() =>
            unlinkIntegration.mutate({
              advisoryOrganizationId,
              integrationId: params.row.integrationId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack alignItems='center' direction='row' spacing={0.5}>
        <Typography variant='h6'>Linked Integrations</Typography>
        <Tooltip title='Controls which third-party integrations are available to this advisory organization. Integrations connect iJoin to external systems (e.g. recordkeepers, payroll providers, custodians) so data can flow between them.'>
          <IconButton>
            <HelpOutlineIcon color='action' />
          </IconButton>
        </Tooltip>
      </Stack>
      <Form methods={linkForm}>
        <Stack alignItems='flex-end' direction='row' spacing={1} sx={{ mb: 2 }}>
          <Autocomplete<IntegrationOption>
            data-testid='integrationToLink'
            hideError
            label='Integration'
            name='integration'
            options={(unlinked.data ?? []).map(i => ({
              label: i.description,
              value: i.integrationId
            }))}
            sx={{ minWidth: 300 }}
          />
          <Tooltip title='Link'>
            <IconButton
              data-testid='linkIntegration'
              disabled={!integrationToLink}
              onClick={() => {
                if (!integrationToLink) return;
                linkIntegration.mutate({
                  advisoryOrganizationId,
                  integrationId: integrationToLink.value
                });
              }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Form>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.integrationId}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } }
        }}
        loading={linked.isFetching}
        pageSizeOptions={[5, 10, 25]}
        rows={linked.data ?? []}
        showColumnMenu
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'linkedIntegrations'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

export const Route = createFileRoute(
  '/organizations/$advisoryOrganizationId/integrations'
)({
  component: IntegrationsTab
});
